import { useQuery } from '@tanstack/react-query';
import { usePrivy } from '@privy-io/react-auth';
import { env } from '@/utils/envs';
import QueryKey from '@/utils/queryKeys';

// --- Linked Account types (mirrors shinkai-web schema) ---

export type LinkedAccount =
  | { type: 'wallet'; address: string; chainType: 'ethereum' | 'solana' }
  | { type: 'email'; address: string }
  | { type: 'google_oauth'; email: string; name?: string; subject: string }
  | { type: 'discord_oauth'; username: string; subject: string }
  | { type: 'twitter_oauth'; username: string; subject: string }
  | { type: 'github_oauth'; username: string; subject: string };

export type UserLinkedAccounts = {
  accounts: LinkedAccount[];
  updatedAt: string;
};

// --- Usage Stats types ---

export type DailyCount = {
  date: string;
  count: number;
};

export type TopUser = {
  userId: string;
  email: string;
  privyId: string | null;
  linkedAccounts: UserLinkedAccounts | null;
  eventCount: number;
};

export type UsageStatsResponse = {
  dailyCounts: DailyCount[];
  topUsers: TopUser[];
};

export type UsageStatsParams = {
  startDate?: string;
  endDate?: string;
  eventName?: string;
  topN?: number;
};

// --- User Activity types ---

export type EventBreakdownItem = {
  eventName: string;
  count: number;
};

export type UserActivityResponse = {
  eventBreakdown: EventBreakdownItem[];
  dailyActivity: DailyCount[];
};

// --- Display name resolution ---

/**
 * Returns the best display name for a user from their linked accounts.
 * Priority: Discord > Twitter > GitHub > Google name > email.
 */
export function getUserDisplayName(u: TopUser): {
  name: string;
  type: 'discord' | 'twitter' | 'github' | 'google' | 'email' | 'privy';
} {
  const accounts = u.linkedAccounts?.accounts ?? [];

  const discord = accounts.find(
    (a): a is Extract<LinkedAccount, { type: 'discord_oauth' }> =>
      a.type === 'discord_oauth',
  );
  if (discord) return { name: discord.username, type: 'discord' };

  const twitter = accounts.find(
    (a): a is Extract<LinkedAccount, { type: 'twitter_oauth' }> =>
      a.type === 'twitter_oauth',
  );
  if (twitter) return { name: `@${twitter.username}`, type: 'twitter' };

  const github = accounts.find(
    (a): a is Extract<LinkedAccount, { type: 'github_oauth' }> =>
      a.type === 'github_oauth',
  );
  if (github) return { name: github.username, type: 'github' };

  const google = accounts.find(
    (a): a is Extract<LinkedAccount, { type: 'google_oauth' }> =>
      a.type === 'google_oauth',
  );
  if (google) return { name: google.name ?? google.email, type: 'google' };

  const emailAccount = accounts.find(
    (a): a is Extract<LinkedAccount, { type: 'email' }> => a.type === 'email',
  );
  if (emailAccount) return { name: emailAccount.address, type: 'email' };

  // Fallback to the user table email
  if (u.email && !u.email.startsWith('privy:') && !u.email.startsWith('wallet:'))
    return { name: u.email, type: 'email' };

  return { name: u.privyId ?? u.email, type: 'privy' };
}

// --- Hooks ---

export const useUsageStats = (params: UsageStatsParams = {}) => {
  const { authenticated, getAccessToken } = usePrivy();

  return useQuery({
    queryKey: [QueryKey.USAGE_STATS, params],
    queryFn: async (): Promise<UsageStatsResponse> => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Missing access token');
      }

      const searchParams = new URLSearchParams();
      if (params.startDate) {
        searchParams.set('startDate', params.startDate);
      }
      if (params.endDate) {
        searchParams.set('endDate', params.endDate);
      }
      if (params.eventName) {
        searchParams.set('eventName', params.eventName);
      }
      if (params.topN) {
        searchParams.set('topN', String(params.topN));
      }

      const queryString = searchParams.toString();
      const url = `${env.VITE_SHINKAI_WEB_API_URL}/api/analytics/usage-stats${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(data.error ?? 'Failed to fetch usage stats');
      }

      return response.json() as Promise<UsageStatsResponse>;
    },
    enabled: authenticated,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
};

export const useUserActivity = ({
  userId,
  startDate,
  endDate,
  enabled = true,
}: {
  userId: string | null;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
}) => {
  const { authenticated, getAccessToken } = usePrivy();

  return useQuery({
    queryKey: [QueryKey.USAGE_STATS, 'user-activity', userId, startDate, endDate],
    queryFn: async (): Promise<UserActivityResponse> => {
      const token = await getAccessToken();
      if (!token) throw new Error('Missing access token');

      const searchParams = new URLSearchParams();
      if (userId) searchParams.set('userId', userId);
      if (startDate) searchParams.set('startDate', startDate);
      if (endDate) searchParams.set('endDate', endDate);

      const url = `${env.VITE_SHINKAI_WEB_API_URL}/api/analytics/user-activity?${searchParams.toString()}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(data.error ?? 'Failed to fetch user activity');
      }

      return response.json() as Promise<UserActivityResponse>;
    },
    enabled: authenticated && enabled && !!userId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
};
